import * as bigInt from 'big-integer';
export type BigInteger = bigInt.BigInteger;

export class MemoryBuffer {

    private totalLength: number = 0;
    private curOffset: number = 0;

    constructor(
        byteArray?: Uint8Array,
        size?: number,
        private _byteArray: Uint8Array = byteArray,
        private _size: number = size
    ) {
        if (!this._byteArray) {
            if (!size) {
                this._byteArray = new Uint8Array(0);
            } else {
                this._byteArray = new Uint8Array(size);
            }
        }

        this.totalLength = this._byteArray.length;
    }


    static makeBigInteger(hi: number, lo: number): BigInteger {
        const shiftLeft32 = bigInt("4294967296");
        const lowPartBigInt = bigInt(lo);
        const highPartBigInt = bigInt(hi);
        let U64 = highPartBigInt.multiply(shiftLeft32);
        U64 = U64.add(lowPartBigInt);
        if (hi & 0x80000000) {
            const maxInt64 = bigInt("ffffffffffffffff", 16);
            U64 = maxInt64.subtract(U64).add(1);
            U64 = bigInt(0).subtract(U64);
        }
        return U64;
    };

    private checkOutOfBounds = (offset: number, length: number, size: number): boolean => {
        let result: boolean = false;
        if (offset + size > length) {
            result = true;
        }
        return result;
    };

    private resize = (offset: number, length: number, size: number): boolean => {
        let overrun = ((offset + size) - length);
        let oldByteArray = new Uint8Array(length);

        for (let i = 0; i < length; i++) {
            oldByteArray[i] = this._byteArray[i];
        }

        this._byteArray = new Uint8Array(length + overrun);

        for (let i = 0; i < length; i++) {
            this._byteArray[i] = oldByteArray[i];
        }

        this.totalLength = this._byteArray.length;

        return true;
    };

    private toUInt = (value: number): number => value >>> 0;

    private makeWideChar = (hi: number, lo: number): number => (hi << 8) | (lo);

    public readCharArray = (size: number): string => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, size)) {
            throw new RangeError("Read index out of range");
        }

        let stringValue = "";
        for (let sv = 0; sv < size; sv += 2) {
            if (this._byteArray[this.curOffset + sv + 1] == 0 && this._byteArray[this.curOffset + sv] == 0) break;
            stringValue += String.fromCharCode(this.makeWideChar(this._byteArray[this.curOffset + sv + 1], this._byteArray[this.curOffset + sv]));
        }

        this.curOffset += size;
        stringValue = stringValue.replace(/\0/g, ''); // remove null termination
        return stringValue;
    };

    public readString = (): string => {
        const stringLength = this.readUInt32BE();
        return this.readCharArray(stringLength);
    };

    public readCompInt = (): number => {
        let result = 0;
        let byteValue = 0x80;
        let position = 0;

        for (; (byteValue & 0x80) != 0; ++position) {
            byteValue = this._byteArray[this.curOffset];
            this.curOffset++;
            const temp = (byteValue & 0x7f);
            result |= temp << (position * 7);
        }

        return result;
    }

    public readUInt8 = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
            throw new RangeError("Read index out of range");
        }
        const value = this._byteArray[this.curOffset];
        this.curOffset++;
        return this.toUInt(value);
    };

    public readInt8 = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
            throw new RangeError("Read index out of range");
        }
        let value = 0;
        if (!(this._byteArray[this.curOffset] & 0x80)) {
            value = this._byteArray[this.curOffset];
        } else {
            value = (0xff - this._byteArray[this.curOffset] + 1) * -1;
        }
        this.curOffset++;
        return value;
    };

    public readUInt16LE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            throw new RangeError("Read index out of range");
        }
        const value = this._byteArray[this.curOffset] | (this._byteArray[this.curOffset + 1] << 8);
        this.curOffset += 2;
        return this.toUInt(value);
    };

    public readUInt16BE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            throw new RangeError("Read index out of range");
        }
        const value = (this._byteArray[this.curOffset] << 8) | this._byteArray[this.curOffset + 1];
        this.curOffset += 2;
        return this.toUInt(value);
    };

    public readInt16LE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            throw new RangeError("Read index out of range");
        }
        const value = this._byteArray[this.curOffset] | (this._byteArray[this.curOffset + 1] << 8);
        this.curOffset += 2;
        return (value & 0x8000) ? (value | 0xFFFF0000) : value;
    };

    public readInt16BE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            throw new RangeError("Read index out of range");
        }
        const value = (this._byteArray[this.curOffset + 1]) | (this._byteArray[this.curOffset] << 8);
        this.curOffset += 2;
        return (value & 0x8000) ? (value | 0xFFFF0000) : value;
    };

    public readUInt32LE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            throw new RangeError("Read index out of range");
        }
        const value = this._byteArray[this.curOffset] |
            (this._byteArray[this.curOffset + 1] << 8) |
            (this._byteArray[this.curOffset + 2] << 16) |
            (this._byteArray[this.curOffset + 3] << 24);

        this.curOffset += 4;
        return this.toUInt(value);
    };

    public readUInt32BE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            throw new RangeError("Read index out of range");
        }
        const value = (this._byteArray[this.curOffset] << 24) +
            (this._byteArray[this.curOffset + 1] << 16) |
            (this._byteArray[this.curOffset + 2] << 8) |
            (this._byteArray[this.curOffset + 3]);

        this.curOffset += 4;
        return this.toUInt(value);
    };

    public readInt32LE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            throw new RangeError("Read index out of range");
        }
        const value = this._byteArray[this.curOffset] |
            (this._byteArray[this.curOffset + 1] << 8) |
            (this._byteArray[this.curOffset + 2] << 16) |
            (this._byteArray[this.curOffset + 3] << 24);
        this.curOffset += 4;
        return value;
    };

    public readInt32BE = (): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            throw new RangeError("Read index out of range");
        }
        const value = (this._byteArray[this.curOffset] << 24) |
            (this._byteArray[this.curOffset + 1] << 16) |
            (this._byteArray[this.curOffset + 2] << 8) |
            (this._byteArray[this.curOffset + 3]);

        this.curOffset += 4;
        return value;
    };

    public readInt64LE = (): BigInteger => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 8)) {
            throw new RangeError("Read index out of range");
        }
        const valueLo = this.readUInt32LE();
        const valueHi = this.readUInt32LE();

        return MemoryBuffer.makeBigInteger(valueHi, valueLo);
    };

    public readInt64BE = (): BigInteger => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 8)) {
            throw new RangeError("Read index out of range");
        }
        const valueHi = this.readUInt32BE();
        const valueLo = this.readUInt32BE();

        return MemoryBuffer.makeBigInteger(valueHi, valueLo);
    };

    public writeStringLE = (value: string): string => {
        // We record the length of the string before the actual string, using 4 bytes
        this.writeInt32BE((value.length + 1) * 2);

        if (this.checkOutOfBounds(this.curOffset, this.totalLength, (value.length + 1) * 2)) {
            this.resize(this.curOffset, this.totalLength, (value.length + 1) * 2);
        }

        for (let i = 0; i < value.length; i++) {
            this._byteArray[this.curOffset] = value.charCodeAt(i);
            this._byteArray[this.curOffset + 1] = (value.charCodeAt(i) >>> 8);
            this.curOffset += 2;
        }
        this._byteArray[this.curOffset] = 0;
        this._byteArray[this.curOffset + 1] = 0;
        this.curOffset += 2;

        return value;
    };

    public writeCompInt = (unsignedValue: number): void => {
        for (; ;) {
            const ch = (unsignedValue & 0x7f);  // get the lowest 7 bits
            unsignedValue >>= 7;
            if (unsignedValue === 0) {
                if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
                    this.resize(this.curOffset, this.totalLength, 1);
                }
                this._byteArray[this.curOffset] = ch;
                this.curOffset++;
                break;
            }

            if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
                this.resize(this.curOffset, this.totalLength, 1);
            }
            this._byteArray[this.curOffset] = (ch | 0x80);
            this.curOffset++;
        }
    };

    public writeUInt8 = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
            this.resize(this.curOffset, this.totalLength, 1);
        }
        this._byteArray[this.curOffset] = value;
        this.curOffset++;
        return value;
    };

    public writeInt8 = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 1)) {
            this.resize(this.curOffset, this.totalLength, 1);
        }
        if (value < 0) {
            value = 0xFF + value + 1;
        }
        this._byteArray[this.curOffset] = value;
        this.curOffset++;
        return value;
    };

    public writeUInt16LE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            this.resize(this.curOffset, this.totalLength, 2);
        }
        this._byteArray[this.curOffset] = value;
        this._byteArray[this.curOffset + 1] = (value >>> 8);
        this.curOffset += 2;
        return value;
    };

    public writeUInt16BE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            this.resize(this.curOffset, this.totalLength, 2);
        }
        this._byteArray[this.curOffset] = (value >>> 8);
        this._byteArray[this.curOffset + 1] = value;
        this.curOffset += 2;
        return value;
    };

    public writeInt16LE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            this.resize(this.curOffset, this.totalLength, 2);
        }
        this._byteArray[this.curOffset] = value;
        this._byteArray[this.curOffset + 1] = (value >>> 8);
        this.curOffset += 2;
        return value;
    };

    public writeInt16BE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 2)) {
            this.resize(this.curOffset, this.totalLength, 2);
        }
        this._byteArray[this.curOffset] = (value >>> 8);
        this._byteArray[this.curOffset + 1] = value;
        this.curOffset += 2;
        return value;
    };

    public writeUInt32LE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            this.resize(this.curOffset, this.totalLength, 4);
        }
        this._byteArray[this.curOffset + 3] = (value >>> 24);
        this._byteArray[this.curOffset + 2] = (value >>> 16);
        this._byteArray[this.curOffset + 1] = (value >>> 8);
        this._byteArray[this.curOffset] = value;
        this.curOffset += 4;
        return value;
    };

    public writeUInt32BE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            this.resize(this.curOffset, this.totalLength, 4);
        }
        this._byteArray[this.curOffset] = (value >>> 24);
        this._byteArray[this.curOffset + 1] = (value >>> 16);
        this._byteArray[this.curOffset + 2] = (value >>> 8);
        this._byteArray[this.curOffset + 3] = value;
        this.curOffset += 4;
        return value;
    };

    public writeInt32LE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            this.resize(this.curOffset, this.totalLength, 4);
        }
        this._byteArray[this.curOffset] = value;
        this._byteArray[this.curOffset + 1] = (value >>> 8);
        this._byteArray[this.curOffset + 2] = (value >>> 16);
        this._byteArray[this.curOffset + 3] = (value >>> 24);
        this.curOffset += 4;
        return value;
    };

    public writeInt32BE = (value: number): number => {
        if (this.checkOutOfBounds(this.curOffset, this.totalLength, 4)) {
            this.resize(this.curOffset, this.totalLength, 4);
        }
        if (value < 0) {
            value = 0xffffffff + value + 1;
        }
        this._byteArray[this.curOffset] = (value >>> 24);
        this._byteArray[this.curOffset + 1] = (value >>> 16);
        this._byteArray[this.curOffset + 2] = (value >>> 8);
        this._byteArray[this.curOffset + 3] = value;
        this.curOffset += 4;
        return value;
    };

    public readBlob = (): Uint8Array => {
        const size = this.readUInt32BE();
        const blob = this._byteArray.subarray(this.curOffset, this.curOffset + size);
        this.curOffset += size;
        return blob;
    };

    public concat = (addBuffer: Uint8Array): void => {
        this.resize(0, this.totalLength, addBuffer.length + this.totalLength);

        const originalLength = this.totalLength - addBuffer.length;
        for (let i = 0; i < addBuffer.length; i++) {
            this._byteArray[i + originalLength] = addBuffer[i];
        }
    };

    public concatByteArray = (addBuffer: any): void => {
        const uint8View = new Uint8Array(addBuffer);
        this.resize(0, this.totalLength, uint8View.byteLength + this.totalLength);

        const originalLength = this.totalLength - uint8View.byteLength;
        this._byteArray.set(uint8View, originalLength);

        this.curOffset = this._byteArray.byteLength;
    };

    public seek = (newPosition: number): void => {
        this.curOffset = newPosition;
    };

    public getPosition = (): number => this.curOffset;

    public getByteArray = (): Uint8Array => this._byteArray;
}
